import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  AccountTree as GraphMLIcon,
  DataObject as JsonIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as FitIcon,
  Refresh as RefreshIcon,
  GridOn as GridIcon,
  Timeline as TimelineIcon,
  Hub as HubIcon,
  ScatterPlot as ScatterIcon,
} from '@mui/icons-material';
import { graphApi } from '../../../services/api/graph.service';

interface GraphToolbarProps {
  onLayoutChange?: (layout: string) => void;
  onExport?: (format: string) => void;
  onExportDialogOpen?: () => void;
  scope: 'personal' | 'organization';
}

/**
 * GraphToolbar Component
 * 
 * Provides controls for graph manipulation and export.
 * 
 * Features:
 * - Export options (PNG, SVG, JSON, GraphML, GEXF)
 * - Layout selection (fcose, cose-bilkent, grid, circle, breadthfirst)
 * - Zoom controls
 * - View reset
 */
const GraphToolbar: React.FC<GraphToolbarProps> = ({
  onLayoutChange,
  onExport,
  onExportDialogOpen,
  scope,
}) => {
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [layoutAnchor, setLayoutAnchor] = useState<null | HTMLElement>(null);

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    // If advanced export dialog is available, open it instead of the inline menu
    if (onExportDialogOpen) {
      onExportDialogOpen();
      return;
    }
    setExportAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchor(null);
  };

  const handleLayoutClick = (event: React.MouseEvent<HTMLElement>) => {
    setLayoutAnchor(event.currentTarget);
  };

  const handleLayoutClose = () => {
    setLayoutAnchor(null);
  };

  const handleExport = async (format: string) => {
    handleExportClose();
    
    const graphCanvas = (window as any).graphCanvas;
    if (!graphCanvas) {
      console.error('Graph canvas not available');
      return;
    }

    try {
      switch (format) {
        case 'png': {
          const blob = await graphCanvas.exportPNG();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `knowledge-graph-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          break;
        }
        
        case 'svg': {
          const svg = graphCanvas.exportSVG();
          const blob = new Blob([svg], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `knowledge-graph-${Date.now()}.svg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          break;
        }
        
        case 'json':
        case 'graphml':
        case 'gexf': {
          // Call backend API for these formats
          const response = await graphApi.exportGraph(scope, {
            format: format as any, // Cast to any since backend supports gexf
            includeStyles: true,
          });
          
          const mimeTypes: Record<string, string> = {
            json: 'application/json',
            graphml: 'application/xml',
            gexf: 'application/xml',
          };
          
          const blob = new Blob([response.data], { type: mimeTypes[format] });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `knowledge-graph-${Date.now()}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          break;
        }
      }
      
      if (onExport) {
        onExport(format);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleLayoutChange = (layout: string) => {
    handleLayoutClose();
    
    const graphCanvas = (window as any).graphCanvas;
    if (graphCanvas) {
      graphCanvas.changeLayout(layout);
    }
    
    if (onLayoutChange) {
      onLayoutChange(layout);
    }
  };

  const handleZoomIn = () => {
    const graphCanvas = (window as any).graphCanvas;
    if (graphCanvas && graphCanvas.cy) {
      const currentZoom = graphCanvas.cy.zoom();
      graphCanvas.cy.zoom(currentZoom * 1.2);
    }
  };

  const handleZoomOut = () => {
    const graphCanvas = (window as any).graphCanvas;
    if (graphCanvas && graphCanvas.cy) {
      const currentZoom = graphCanvas.cy.zoom();
      graphCanvas.cy.zoom(currentZoom * 0.8);
    }
  };

  const handleFit = () => {
    const graphCanvas = (window as any).graphCanvas;
    if (graphCanvas) {
      graphCanvas.fit();
    }
  };

  const handleReset = () => {
    const graphCanvas = (window as any).graphCanvas;
    if (graphCanvas) {
      graphCanvas.resetZoom();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        backgroundColor: '#fff',
      }}
    >
      {/* Export Menu */}
      <Tooltip title="Export Graph">
        <IconButton onClick={handleExportClick} size="small">
          <DownloadIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => handleExport('png')}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PNG</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('svg')}>
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as SVG</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleExport('json')}>
          <ListItemIcon>
            <JsonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as JSON</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('graphml')}>
          <ListItemIcon>
            <GraphMLIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as GraphML</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('gexf')}>
          <ListItemIcon>
            <JsonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as GEXF</ListItemText>
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem />

      {/* Layout Menu */}
      <Tooltip title="Change Layout">
        <IconButton onClick={handleLayoutClick} size="small">
          <HubIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={layoutAnchor}
        open={Boolean(layoutAnchor)}
        onClose={handleLayoutClose}
      >
        <MenuItem onClick={() => handleLayoutChange('fcose')}>
          <ListItemIcon>
            <HubIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Force-Directed (fcose)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleLayoutChange('cose-bilkent')}>
          <ListItemIcon>
            <ScatterIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Compound Spring (cose-bilkent)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleLayoutChange('grid')}>
          <ListItemIcon>
            <GridIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Grid</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleLayoutChange('circle')}>
          <ListItemIcon>
            <TimelineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Circle</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleLayoutChange('breadthfirst')}>
          <ListItemIcon>
            <GraphMLIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Breadth First</ListItemText>
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem />

      {/* Zoom Controls */}
      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Zoom In">
          <Button onClick={handleZoomIn}>
            <ZoomInIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <Button onClick={handleZoomOut}>
            <ZoomOutIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Fit to View">
          <Button onClick={handleFit}>
            <FitIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Reset View">
          <Button onClick={handleReset}>
            <RefreshIcon fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>
    </div>
  );
};

export default GraphToolbar;